import { fetchLiveTrades, fetchStar, NormalizedStar } from 'api/galaxyApi';
import axios from 'axios';
import { DEFAULT_VIEW_MODE, VIEW_TRANSITION_SPEED } from 'consts/viewModes';
import { makeAutoObservable, runInAction } from 'mobx';
import { AssistantStore } from 'store/assistant/AssistantStore';
import { CommonStore } from 'store/common/CommonStore';
import { Web3Store } from 'store/web3/Web3Store';
import { Wallet } from 'types/copyTrading';
import { FeedEvent, TradeEvent } from 'types/event';
import { StarData, ViewMode, ViewTransition } from 'types/galaxy';

import { NewsResponse, NewsStory } from './types';

// ── News constants ────────────────────────────────────────────────────────────

const NEWS_API_BASE = 'http://37.27.237.160:8078';
const NEWS_STORAGE_KEY = 'wowmax_news_read';
const INITIAL_FETCH_DELAY = 5_000;
const POLL_INTERVAL = 60_000;
const MAX_READ_STORIES = 30;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PendingEffect {
  traderIndex: number;
  type: 'supernova' | 'collapse';
  intensity: number;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export class GalaxyStore {
  // ── View ──────────────────────────────────────────────────────────────────
  currentViewMode: ViewMode = DEFAULT_VIEW_MODE;
  viewTransition: ViewTransition = { active: false, progress: 0 };
  isViewSelectorOpen = false;
  isLoading = true;
  loadingStatus = 'Connecting to API...';
  loadingProgress = 0;
  loadingCount = '';

  // ── Traders ───────────────────────────────────────────────────────────────
  traders: StarData[] = [];
  traderIndexByAddress: Map<string, number> = new Map();
  recentEvents: FeedEvent[] = [];
  lastTradeBlock = 0;
  pendingEffects: PendingEffect[] = [];
  selectedTraderIndex: number | null = null;

  // ── Navigation ──────────────────────────────────────────────────────────
  portalFlightRequest = 0;
  wormholeTravelRequest = 0;
  targetFrameActive = false;
  targetFrameX = 0;
  targetFrameY = 0;

  // ── AI Mind / Sophie-driven navigation ──────────────────────────────────
  // True when at least one loaded star carries server-side mind coordinates.
  mindAvailable = false;
  // Camera fly-to requests (seq bumps so repeated flights to the same star fire).
  flyToStarIndex: number | null = null;
  flyToStarSeq = 0;
  // Highlight a subset of stars by address; null = clear (all bright).
  highlightAddresses: string[] | null = null;
  highlightSeq = 0;
  // Guest star: a searched wallet outside the displayed set (marker + panel).
  guestStar: NormalizedStar | null = null;
  guestSeq = 0;

  // ── Copy Trading ────────────────────────────────────────────────────────
  scannedWallets: Wallet[] = [];
  totalWalletsFound = 0;

  // ── News ──────────────────────────────────────────────────────────────────
  newsStories: NewsStory[] = [];
  readNewsStories: NewsStory[] = [];
  totalNewEpisodes = 0;
  isNewsModalOpen = false;

  constructor(
    private web3: Web3Store,
    private common: CommonStore,
    private assistant: AssistantStore
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.loadReadStories();
    this.startNewsPolling();
  }

  // ── View ──────────────────────────────────────────────────────────────────

  setViewMode(mode: ViewMode) {
    if (this.currentViewMode === mode) return;
    this.currentViewMode = mode;
    this.startViewTransition();
  }

  startViewTransition() {
    this.viewTransition = { active: true, progress: 0 };
  }

  updateViewTransition(deltaTime: number) {
    if (!this.viewTransition.active) return;
    this.viewTransition.progress += VIEW_TRANSITION_SPEED * deltaTime * 60;
    if (this.viewTransition.progress >= 1) {
      this.viewTransition = { active: false, progress: 1 };
    }
  }

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  updateLoadingProgress(status: string, progress: number, count?: number) {
    this.loadingStatus = status;
    this.loadingProgress = progress;
    this.loadingCount = count ? `${count.toLocaleString()} traders` : '';
  }

  toggleViewSelector() {
    this.isViewSelectorOpen = !this.isViewSelectorOpen;
  }

  closeViewSelector() {
    this.isViewSelectorOpen = false;
  }

  // ── Traders ───────────────────────────────────────────────────────────────

  async pollLiveTrades() {
    try {
      const data = await fetchLiveTrades(20, this.lastTradeBlock || undefined);

      if (data.trades && data.trades.length > 0) {
        const trades = [...data.trades].reverse();
        for (const trade of trades) {
          if (trade.sell_block > this.lastTradeBlock) {
            this.processLiveTrade(trade);
          }
        }
        this.lastTradeBlock = data.last_block;
      }

      this.web3.setCurrentBlock(data.current_block);
    } catch (error) {
      console.error('Failed to poll live trades:', error);
    }
  }

  private processLiveTrade(trade: {
    trader: string;
    token: string;
    symbol: string;
    logo_url?: string;
    pnl_usd: number;
    sell_block: number;
  }) {
    if (trade.pnl_usd < 20) return;

    const traderAddress = trade.trader.toLowerCase();
    const traderIndex = this.traderIndexByAddress.get(traderAddress);

    const isProfit = true;
    const amount = trade.pnl_usd;

    const eventData: TradeEvent = {
      trader: {
        id: traderIndex !== undefined ? traderIndex : -1,
        wallet: trade.trader,
        pnl: traderIndex !== undefined ? this.traders[traderIndex].pnl : 0,
        roi: traderIndex !== undefined ? this.traders[traderIndex].roi : 0,
        f_roi: traderIndex !== undefined ? this.traders[traderIndex].f_roi : 0,
        totalTrades: traderIndex !== undefined ? this.traders[traderIndex].totalTrades : 0,
      },
      token: {
        address: trade.token,
        symbol: trade.symbol || 'TOKEN',
        logo_url: trade.logo_url,
      },
      type: isProfit ? 'profit' : 'loss',
      amount: isProfit ? amount : -amount,
      sell_block: trade.sell_block,
      pnl_usd: trade.pnl_usd,
    };

    if (traderIndex !== undefined) {
      const intensity = Math.min(amount / 10000, 1);
      this.pendingEffects.push({
        traderIndex,
        type: isProfit ? 'supernova' : 'collapse',
        intensity,
      });
    }

    this.recentEvents.unshift(eventData);
    if (this.recentEvents.length > 6) {
      this.recentEvents.pop();
    }
  }

  consumePendingEffects(): PendingEffect[] {
    const effects = this.pendingEffects.slice();
    this.pendingEffects = [];
    return effects;
  }

  getTraderByAddress(address: string): StarData | undefined {
    const index = this.traderIndexByAddress.get(address.toLowerCase());
    return index !== undefined ? this.traders[index] : undefined;
  }

  getTraderByIndex(index: number): StarData | undefined {
    return this.traders[index];
  }

  openTraderCard(traderIndex: number) {
    this.selectedTraderIndex = traderIndex;
  }

  closeTraderCard() {
    this.selectedTraderIndex = null;
    if (this.guestStar) {
      this.guestStar = null;
      this.guestSeq++;
    }
  }

  // ── Navigation ──────────────────────────────────────────────────────────

  requestPortalFlight() {
    this.portalFlightRequest++;
  }

  requestWormholeTravel() {
    this.wormholeTravelRequest++;
  }

  setTargetFrame(active: boolean, x: number = 0, y: number = 0) {
    this.targetFrameActive = active;
    this.targetFrameX = x;
    this.targetFrameY = y;
  }

  // ── AI Mind / Sophie-driven navigation ──────────────────────────────────

  setMindAvailable(available: boolean) {
    this.mindAvailable = available;
  }

  requestFlyToStar(index: number) {
    this.flyToStarIndex = index;
    this.flyToStarSeq++;
  }

  setHighlight(addresses: string[] | null) {
    this.highlightAddresses = addresses && addresses.length > 0 ? addresses : null;
    this.highlightSeq++;
  }

  /**
   * Find ANY wallet (all 2.5M in pm_traders, not just the displayed set).
   * Displayed star → camera flight + trader card. Off-screen wallet →
   * fetched as a guest star: Mind view, pulsing marker, trader card.
   */
  async searchWallet(rawAddress: string): Promise<'flown' | 'guest' | 'not_found' | 'invalid'> {
    const address = rawAddress.trim().toLowerCase();
    if (!/^0x[0-9a-f]{40}$/.test(address)) return 'invalid';

    const index = this.traderIndexByAddress.get(address);
    if (index !== undefined) {
      this.requestFlyToStar(index);
      this.openTraderCard(index);
      return 'flown';
    }

    let star: NormalizedStar | null = null;
    try {
      star = await fetchStar(address);
    } catch {
      return 'not_found';
    }
    if (!star) return 'not_found';

    runInAction(() => {
      if (this.currentViewMode !== 'mind') {
        this.setViewMode('mind');
      }
      this.selectedTraderIndex = null;
      this.guestStar = star;
      this.guestSeq++;
    });
    return 'guest';
  }

  // ── Copy Trading ────────────────────────────────────────────────────────

  togglePilotMode() {
    if (this.common.mode === 'pilot' || this.common.mode === 'autopilot') {
      this.scannedWallets = [];
      this.totalWalletsFound = 0;
      this.common.setMode('main');
    } else {
      this.common.setMode('pilot');
    }
  }

  toggleAutopilot() {
    if (this.common.mode === 'pilot') {
      this.common.setMode('autopilot');
    } else if (this.common.mode === 'autopilot') {
      this.scannedWallets = [];
      this.common.setMode('pilot');
    }
  }

  addScannedWallets(wallets: { address: string; roi: number }[]) {
    this.scannedWallets = wallets;
    this.totalWalletsFound += wallets.length;
  }

  setTotalWalletsFound(count: number) {
    this.totalWalletsFound = count;
  }

  // ── News ──────────────────────────────────────────────────────────────────

  private startNewsPolling() {
    setTimeout(() => {
      this.fetchNotifications();
      setInterval(() => this.fetchNotifications(), POLL_INTERVAL);
    }, INITIAL_FETCH_DELAY);
  }

  async fetchNotifications() {
    try {
      const userId = this.assistant.ensureUserId();
      const { data } = await axios.get<NewsResponse>(`${NEWS_API_BASE}/notifications`, {
        params: { user_id: userId },
      });
      this.totalNewEpisodes = data.total_new_episodes;
      this.newsStories = data.notifications;
    } catch (err) {
      console.error('[News] Fetch failed:', err);
    }
  }

  openNewsModal() {
    this.isNewsModalOpen = true;
  }

  closeNewsModal() {
    this.isNewsModalOpen = false;
    if (this.newsStories.length > 0) {
      this.mergeToRead(this.newsStories);
      this.newsStories = [];
      this.totalNewEpisodes = 0;
      this.persistReadStories();
      this.sendReadRequest();
    }
  }

  private async sendReadRequest() {
    try {
      const userId = this.assistant.ensureUserId();
      await axios.post(`${NEWS_API_BASE}/notifications/read`, { user_id: userId });
    } catch (err) {
      console.error('[News] Mark as read failed:', err);
    }
  }

  private mergeToRead(stories: NewsStory[]) {
    for (const story of stories) {
      const existing = this.readNewsStories.find((s) => s.story_id === story.story_id);
      if (existing) {
        const existingNums = new Set(existing.episodes.map((e) => e.episode_number));
        for (const ep of story.episodes) {
          if (!existingNums.has(ep.episode_number)) {
            existing.episodes.push(ep);
          }
        }
        existing.episodes.sort((a, b) => b.episode_number - a.episode_number);
      } else {
        this.readNewsStories.push({
          ...story,
          episodes: [...story.episodes].sort((a, b) => b.episode_number - a.episode_number),
        });
      }
    }
    if (this.readNewsStories.length > MAX_READ_STORIES) {
      this.readNewsStories = this.readNewsStories.slice(-MAX_READ_STORIES);
    }
  }

  private persistReadStories() {
    try {
      localStorage.setItem(NEWS_STORAGE_KEY, JSON.stringify(this.readNewsStories));
    } catch {
      // localStorage might be full or unavailable
    }
  }

  private loadReadStories() {
    try {
      const raw = localStorage.getItem(NEWS_STORAGE_KEY);
      if (raw) this.readNewsStories = JSON.parse(raw) as NewsStory[];
    } catch {
      // corrupted data
    }
  }
}
